/**
 * useLandAutofill — master hook for the land autofill modal.
 *
 * Stage machine: 'configure' → 'review'
 *
 * Owns all modal state: stage, config, proposal, and F-028 MDFC state.
 * Reads from useDeckStore and useLandPrefsStore.
 * Generation is synchronous (Karsten-based, no Web Worker).
 */

import { useState, useMemo, useCallback } from 'react';
import type {
  Color,
  Card,
  LandCategory,
  LandCategoryAllocation,
  ProposedLand,
  GenerationMode,
  HeuristicHint,
} from '@mtg-deck-tool/shared';
import { isLand, isUtilityLand } from '@mtg-deck-tool/shared';
import { useDeckStore } from '../stores/deck-store.js';
import { useLandPrefsStore } from '../stores/land-prefs-store.js';
import {
  buildCategoryAllocations,
  computeHeuristicHint,
  detectTribalSupport,
  getEligibleCards,
} from '../lib/land-generator.js';
import { LAND_CATALOG } from '../data/land-catalog.js';
import { classifySpells, classifyCommanders } from '../lib/land-sim/sim-classifier.js';
import { parseManaRequirements } from '../lib/land-sim/sim-classifier.js';
import { buildSimLandCardWithCategory, selectLandsKarsten } from '../lib/land-sim/sim-selection.js';
import type { KarstenSelectionInput } from '../lib/land-sim/sim-selection.js';
import {
  expandHybridPipCounts,
  computeKarstenSources,
  buildLandMultiset,
} from '../lib/land-sim/karsten-sources.js';
import { computeLandDiff } from '../lib/land-sim/land-diff.js';
import type { KarstenSourceRow, ManaOverviewRow, LandMultiset } from '../lib/land-sim/karsten-types.js';

// ─── Types ────────────────────────────────────────────────────────────────────

/** Parameters passed to startGeneration(). */
export interface GenerationParams {
  targetCount: number;
  enabledCategories: LandCategory[];
  mode: GenerationMode;
  stapleNames: Set<string>;
  keptUtilityNames: Set<string>;
  minBasicsPerColor: Partial<Record<Color, number>>;
  /** Optional per-category min/max constraints. */
  categoryConstraints?: Map<LandCategory, { min?: number; max?: number }>;
}

export interface UseLandAutofillReturn {
  // Stage
  stage: 'configure' | 'review';
  goToConfigure: () => void;

  // Config state
  targetCount: number;
  setTargetCount: (n: number) => void;
  mode: GenerationMode;
  setMode: (m: GenerationMode) => void;
  allocations: LandCategoryAllocation[];
  toggleCategory: (category: LandCategory) => void;

  // Explicit basic count (not derived from "remaining slots")
  basicCount: number;
  setBasicCount: (n: number) => void;

  // Review state
  proposal: ProposedLand[];
  removeProposedLand: (name: string, basicColor?: Color) => void;
  adjustBasicCount: (color: Color, delta: number) => void;

  // Generation
  startGeneration: (params: GenerationParams) => void;
  generationError: string | null;

  // Heuristic
  hint: HeuristicHint;

  // Existing land state
  existingLandCount: number;
  existingLandsByCategory: Map<LandCategory, string[]>;
  existingBasicCount: number;
  existingUtilityCount: number;
  existingUtilityLandNames: string[];

  /**
   * Commit the accepted proposal to the deck store.
   * The modal is responsible for resolving card names to Card objects,
   * then passes the resolved cards here.
   *
   * In "generate" mode: removes all existing land entries first, then bulk-adds.
   * In "complete" mode: bulk-adds only the new (non-alreadyInDeck) proposals.
   */
  handleAccept: (resolvedCards: Array<{ card: Card; quantity: number }>) => void;

  // Tribal detection
  hasTribalSupport: boolean;

  // F-028 MDFC state
  selectedMdfcs: Set<string>;
  toggleMdfc: (name: string) => void;
  mdfcOverLimit: boolean;

  // Panel data (set after generation, read by modal for proposal store)
  panelData: {
    karstenRows: import('../lib/land-sim/karsten-types.js').KarstenSourceRow[];
    manaOverviewRows: import('../lib/land-sim/karsten-types.js').ManaOverviewRow[];
    landDiff: import('../lib/land-sim/karsten-types.js').LandDiffResult | null;
    beforeSnapshot: import('../lib/land-sim/karsten-types.js').LandMultiset;
  } | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Count the total basic land slots in the current proposal.
 */
function countBasicSlots(proposal: ProposedLand[]): number {
  return proposal
    .filter((p) => p.category === 'basic' && !p.alreadyInDeck)
    .reduce((sum, p) => sum + p.quantity, 0);
}

/**
 * Build a mapping from category → list of land names currently in the deck.
 */
const BASIC_LAND_NAMES_SET = new Set(['Plains', 'Island', 'Swamp', 'Mountain', 'Forest']);

// isUtilityLand is imported from @mtg-deck-tool/shared (extracted in T-EXP-003c, AD-079)

function buildExistingLandsByCategory(
  entries: ReturnType<typeof useDeckStore.getState>['entries'],
): {
  existingLandCount: number;
  existingLandsByCategory: Map<LandCategory, string[]>;
  existingBasicCount: number;
  existingUtilityCount: number;
  existingUtilityLandNames: string[];
} {
  const landEntries = entries.filter(
    (e) => !e.isCommander && isLand(e.card),
  );

  const byCategory = new Map<LandCategory, string[]>();
  let nonBasicCount = 0;
  let basicCount = 0;
  let utilityCount = 0;

  const nameToCat = new Map<string, LandCategory>();
  for (const cycle of LAND_CATALOG) {
    for (const entry of cycle.entries) {
      if (!nameToCat.has(entry.name)) {
        nameToCat.set(entry.name, cycle.category);
      }
    }
  }

  for (const entry of landEntries) {
    if (BASIC_LAND_NAMES_SET.has(entry.card.name)) {
      basicCount += entry.quantity;
      continue;
    }

    const cat = nameToCat.get(entry.card.name);
    if (cat && cat !== 'basic') {
      nonBasicCount += entry.quantity;
      const existing = byCategory.get(cat) ?? [];
      byCategory.set(cat, [...existing, entry.card.name]);
    } else {
      // Any non-catalog, non-basic land is a utility land
      utilityCount += entry.quantity;
    }
  }

  const utilityLandNames: string[] = [];
  for (const entry of landEntries) {
    if (BASIC_LAND_NAMES_SET.has(entry.card.name)) continue;
    const cat = nameToCat.get(entry.card.name);
    if (!cat) {
      // Any non-catalog, non-basic land is utility
      utilityLandNames.push(entry.card.name);
    }
  }

  return {
    existingLandCount: nonBasicCount,
    existingLandsByCategory: byCategory,
    existingBasicCount: basicCount,
    existingUtilityCount: utilityCount,
    existingUtilityLandNames: utilityLandNames,
  };
}

// ─── Selection candidate builders ─────────────────────────────────────────────

const COLOR_TO_BASIC: Record<string, string> = {
  W: 'Plains', U: 'Island', B: 'Swamp', R: 'Mountain', G: 'Forest',
};

const BASIC_NAMES: Record<string, string> = {
  W: 'Plains', U: 'Island', B: 'Swamp', R: 'Mountain', G: 'Forest',
};

/**
 * Build SimLandCard[] candidates from enabled categories for a given color identity.
 */
function buildSelectionCandidates(
  enabledCategories: LandCategory[],
  colorIdentity: Color[],
  hasTribalSupport: boolean,
  stapleNames: Set<string>,
  excludeNames: Set<string>,
): Array<import('../lib/land-sim/sim-types.js').SimLandCard & { _category: LandCategory }> {
  const candidates: Array<import('../lib/land-sim/sim-types.js').SimLandCard & { _category: LandCategory }> = [];

  for (const cycle of LAND_CATALOG) {
    if (!enabledCategories.includes(cycle.category)) continue;
    if (cycle.category === 'basic') continue; // basics are handled in Phase 2

    const eligible = getEligibleCards(cycle, colorIdentity, hasTribalSupport);
    for (const entry of eligible) {
      if (excludeNames.has(entry.name)) continue;
      // Unselected staple (rainbow) lands must not enter the candidate pool
      if (cycle.category === 'rainbow' && !stapleNames.has(entry.name)) continue;

      const simCard = buildSimLandCardWithCategory(entry, cycle);
      candidates.push(simCard as import('../lib/land-sim/sim-types.js').SimLandCard & { _category: LandCategory });
    }
  }

  return candidates;
}

/**
 * Build locked land candidates from the existing deck (complete mode).
 *
 * Utility lands (Reliquary Tower, MDFCs, etc.) are NOT in the catalog and
 * were previously silently dropped. For catalog-missing names we create a
 * minimal SimLandCard from deck card data so the algorithm treats them as
 * occupied slots that produce mana matching their color identity.
 */
function buildLockedCandidates(
  existingLandsByCategory: Map<LandCategory, string[]>,
  enabledCategories: LandCategory[],
  stapleNames: Set<string>,
  keptUtilityNames: Set<string>,
  nameToCard: Map<string, import('@mtg-deck-tool/shared').Card>,
): Array<import('../lib/land-sim/sim-types.js').SimLandCard & { _category: LandCategory }> {
  const nameToCycleEntry = new Map<string, { entry: Parameters<typeof buildSimLandCardWithCategory>[0]; cycle: Parameters<typeof buildSimLandCardWithCategory>[1] }>();
  for (const cycle of LAND_CATALOG) {
    for (const entry of cycle.entries) {
      nameToCycleEntry.set(entry.name, { entry, cycle });
    }
  }

  const locked: Array<import('../lib/land-sim/sim-types.js').SimLandCard & { _category: LandCategory }> = [];
  const allEnabledNames = new Set<string>([...stapleNames, ...keptUtilityNames]);

  for (const [category, names] of existingLandsByCategory) {
    if (!enabledCategories.includes(category)) continue;
    for (const name of names) {
      // Deselected staple (rainbow) lands must not be locked in
      if (category === 'rainbow' && !stapleNames.has(name)) continue;
      allEnabledNames.add(name);
    }
  }

  for (const name of allEnabledNames) {
    const found = nameToCycleEntry.get(name);
    if (found) {
      const simCard = buildSimLandCardWithCategory(found.entry, found.cycle);
      locked.push(simCard as import('../lib/land-sim/sim-types.js').SimLandCard & { _category: LandCategory });
    } else {
      // Utility land not in catalog — determine produced colors from oracle text.
      const card = nameToCard.get(name);
      const oracle = (card?.oracleText ?? '').toLowerCase();

      // Rule: if oracle mentions "mana of any color", treat as rainbow (all identity colors).
      // This covers Command Tower, City of Brass, Mana Confluence, Exotic Orchard, etc.
      // Also covers "sac: add any color" type effects.
      const isAnyColor = oracle.includes('mana of any color') || oracle.includes('any combination of colors');

      let producedColors: Set<Color>;
      if (isAnyColor) {
        // Rainbow — empty producedColors triggers all-identity in computeKarstenSources
        producedColors = new Set<Color>();
      } else {
        // Parse specific mana symbols from oracle text: {W}, {U}, {B}, {R}, {G}
        const colors = new Set<Color>();
        const symbolMatches = oracle.matchAll(/\{([wubrgWUBRG])\}/g);
        for (const m of symbolMatches) {
          const sym = m[1]!.toUpperCase();
          if (['W', 'U', 'B', 'R', 'G'].includes(sym)) {
            colors.add(sym as Color);
          }
        }
        // If no colored mana symbols found, use 'C' sentinel so it doesn't
        // count as a source for any color (e.g., Maze of Ith, Reliquary Tower)
        producedColors = colors.size > 0 ? colors : new Set<Color>(['C' as Color]);
      }

      const minimalCard: import('../lib/land-sim/sim-types.js').SimLandCard & { _category: LandCategory } = {
        name,
        producedColors,
        basicLandTypes: new Set(),
        isLand: true,
        alwaysUntapped: true,
        alwaysTapped: false,
        isFetch: false,
        isBasic: false,
        isVerge: false,
        _category: 'rainbow' as LandCategory,
      };
      locked.push(minimalCard);
    }
  }

  return locked;
}

/**
 * Convert selectedLandNames from the selection result into ProposedLand[].
 */
function buildProposalFromNames(
  selectedLandNames: string[],
  existingDeckNames: Set<string>,
): ProposedLand[] {
  const nameToCat = new Map<string, LandCategory>();
  for (const cycle of LAND_CATALOG) {
    for (const entry of cycle.entries) {
      if (!nameToCat.has(entry.name)) {
        nameToCat.set(entry.name, cycle.category);
      }
    }
  }

  // Basics: group by color
  const basicColorCounts = new Map<Color, number>();
  const nonBasicNames: string[] = [];

  for (const name of selectedLandNames) {
    const cat = nameToCat.get(name);
    if (BASIC_LAND_NAMES_SET.has(name)) {
      // Known basic land name — group by color
      const color = Object.entries(BASIC_NAMES).find(([, n]) => n === name)?.[0] as Color | undefined;
      if (color) {
        basicColorCounts.set(color, (basicColorCounts.get(color) ?? 0) + 1);
      }
    } else if (cat === 'basic') {
      // Catalog entry marked as basic but not a standard basic name — treat as non-basic
      nonBasicNames.push(name);
    } else {
      // Non-basic (catalog or utility land not in catalog)
      nonBasicNames.push(name);
    }
  }

  const result: ProposedLand[] = [];

  // Non-basics first
  for (const name of nonBasicNames) {
    const cat = nameToCat.get(name) ?? 'rainbow';
    result.push({
      name,
      category: cat,
      quantity: 1,
      alreadyInDeck: existingDeckNames.has(name),
    });
  }

  // Basics grouped
  for (const [color, quantity] of basicColorCounts) {
    result.push({
      name: BASIC_NAMES[color]!,
      category: 'basic',
      basicColor: color,
      quantity,
      alreadyInDeck: false,
    });
  }

  return result;
}

// ─── Panel data builders ───────────────────────────────────────────────────────

/**
 * Build KarstenSourceRow[] from selection output data.
 * Status thresholds: on-target >= target, slightly-below >= strictFloor, else below-minimum.
 */
function buildKarstenSourceRows(
  colorIdentity: Color[],
  landSources: Map<Color, number>,
  karstenTargets: import('../lib/land-sim/karsten-types.js').KarstenTargets,
): KarstenSourceRow[] {
  return colorIdentity.map((color) => {
    const ls = landSources.get(color) ?? 0;
    const target = karstenTargets.targets.get(color) ?? 0;
    const strictFloor = karstenTargets.strictFloors.get(color) ?? 0;

    let status: KarstenSourceRow['status'];
    if (ls >= target) {
      status = 'on-target';
    } else if (ls >= strictFloor) {
      status = 'slightly-below';
    } else {
      status = 'below-minimum';
    }

    return {
      color,
      landSources: ls,
      nonLandSources: 0,
      totalSources: ls,
      target,
      strictFloor,
      status,
    };
  });
}

/**
 * Build ManaOverviewRow[] from pip counts and land sources.
 * Land-only — no dork/rock/ramp sources.
 */
function buildManaOverviewRows(
  colorIdentity: Color[],
  pipCounts: Map<Color, number>,
  landSources: Map<Color, number>,
  karstenTargets: import('../lib/land-sim/karsten-types.js').KarstenTargets,
  totalLandCount: number,
): ManaOverviewRow[] {
  const totalPips = Array.from(pipCounts.values()).reduce((s, v) => s + v, 0);

  return colorIdentity.map((color) => {
    const pips = pipCounts.get(color) ?? 0;
    const ls = landSources.get(color) ?? 0;
    const target = karstenTargets.targets.get(color) ?? 0;

    const pipDemandPct = totalPips > 0 ? (pips / totalPips) * 100 : 0;
    const productionPct = totalLandCount > 0 ? (ls / totalLandCount) * 100 : 0;
    const diffPct = productionPct - pipDemandPct;

    return {
      color,
      pipDemandPct: Math.round(pipDemandPct * 10) / 10,
      productionPct: Math.round(productionPct * 10) / 10,
      diffPct: Math.round(diffPct * 10) / 10,
      landSources: ls,
      nonLandSources: 0,
      target,
    };
  });
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useLandAutofill(): UseLandAutofillReturn {
  const entries = useDeckStore((s) => s.entries);
  const colorIdentity = useDeckStore((s) => s.colorIdentity);
  const bulkAddCards = useDeckStore((s) => s.bulkAddCards);
  const removeLandEntries = useDeckStore((s) => s.removeLandEntries);

  const enabledCategoriesFromPrefs = useLandPrefsStore((s) => s.enabledCategories);
  const lastLandCount = useLandPrefsStore((s) => s.lastLandCount);
  const setLastLandCountInPrefs = useLandPrefsStore((s) => s.setLastLandCount);
  const toggleCategoryInPrefs = useLandPrefsStore((s) => s.toggleCategory);

  // ── Modal stage ──
  const [stage, setStage] = useState<'configure' | 'review'>('configure');

  // ── Config state ──
  const [targetCount, setTargetCountLocal] = useState<number>(lastLandCount);
  const [mode, setModeLocal] = useState<GenerationMode>('generate');

  // ── Explicit basic count ──
  const [basicCount, setBasicCountLocal] = useState<number>(() =>
    Math.max(1, colorIdentity.length),
  );

  // ── Review state ──
  const [proposal, setProposal] = useState<ProposedLand[]>([]);

  // ── Generation state ──
  const [generationError, setGenerationError] = useState<string | null>(null);

  // ── Panel data (passed to proposal store via modal effect) ──
  const [panelData, setPanelData] = useState<UseLandAutofillReturn['panelData']>(null);

  // ── F-028 MDFC state ──
  const [selectedMdfcs, setSelectedMdfcs] = useState<Set<string>>(new Set());

  // ── Memoized computations ──
  const hasTribalSupport = useMemo(() => detectTribalSupport(entries), [entries]);
  const hint = useMemo(() => computeHeuristicHint(entries), [entries]);

  const existingData = useMemo(() => buildExistingLandsByCategory(entries), [entries]);
  const { existingLandCount, existingLandsByCategory, existingBasicCount, existingUtilityCount } = existingData;
  const existingUtilityData = useMemo(() => ({ names: existingData.existingUtilityLandNames }), [existingData]);

  const allocations = useMemo(() => {
    return buildCategoryAllocations(
      enabledCategoriesFromPrefs,
      colorIdentity,
      hasTribalSupport,
    );
  }, [enabledCategoriesFromPrefs, colorIdentity, hasTribalSupport]);

  // ── Target count setter ──
  const setTargetCount = useCallback(
    (n: number) => {
      const clamped = Math.max(1, Math.min(100, n));
      setTargetCountLocal(clamped);
      setLastLandCountInPrefs(clamped);
    },
    [setLastLandCountInPrefs],
  );

  // ── Mode setter ──
  const setMode = useCallback((m: GenerationMode) => {
    setModeLocal(m);
  }, []);

  // ── Toggle category ──
  const toggleCategory = useCallback(
    (category: LandCategory) => {
      if (category === 'basic') return;
      toggleCategoryInPrefs(category);
    },
    [toggleCategoryInPrefs],
  );

  const goToConfigure = useCallback(() => {
    setStage('configure');
  }, []);

  // ── Generation: start ──
  const startGeneration = useCallback(
    (params: GenerationParams) => {
      setGenerationError(null);
      setPanelData(null);

      try {
        // Snapshot existing deck names for alreadyInDeck tracking
        const existingDeckNames = new Set(
          entries
            .filter((e) => isLand(e.card))
            .map((e) => e.card.name),
        );

        // Build name → Card map for utility lands not in the catalog
        const nameToCard = new Map<string, import('@mtg-deck-tool/shared').Card>();
        for (const e of entries) {
          if (!nameToCard.has(e.card.name)) {
            nameToCard.set(e.card.name, e.card);
          }
        }

        // Classify deck cards
        const commanderEntries = entries.filter((e) => e.isCommander);
        const nonLandEntries = entries.filter(
          (e) => !e.isCommander && !isLand(e.card),
        );
        const spells = classifySpells(nonLandEntries);
        const commanders = classifyCommanders(commanderEntries);

        // Compute pip counts (with quantity multiplier, hybrid expansion)
        const rawPips = new Map<string, number>();
        for (const entry of nonLandEntries) {
          const manaCost = entry.card.manaCost ?? entry.card.cardFaces?.[0]?.manaCost ?? '';
          const { requirements } = parseManaRequirements(manaCost);
          for (const [key, count] of requirements.entries()) {
            rawPips.set(key, (rawPips.get(key) ?? 0) + count * entry.quantity);
          }
        }
        // Include commanders (without quantity multiplier — each commander is 1 card)
        for (const entry of commanderEntries) {
          const manaCost = entry.card.manaCost ?? entry.card.cardFaces?.[0]?.manaCost ?? '';
          const { requirements } = parseManaRequirements(manaCost);
          for (const [key, count] of requirements.entries()) {
            rawPips.set(key, (rawPips.get(key) ?? 0) + count);
          }
        }
        const pipCounts = expandHybridPipCounts(rawPips);

        // Snapshot before-lands
        const beforeSnapshot = buildLandMultiset(entries);

        // Build candidates
        const candidates = buildSelectionCandidates(
          params.enabledCategories,
          colorIdentity,
          hasTribalSupport,
          params.stapleNames,
          new Set([...params.stapleNames, ...params.keptUtilityNames]),
        );

        // Locked lands (complete mode or staples/utilities)
        const lockedLands = params.mode === 'complete'
          ? buildLockedCandidates(
              existingLandsByCategory,
              params.enabledCategories,
              params.stapleNames,
              params.keptUtilityNames,
              nameToCard,
            )
          : buildLockedCandidates(
              new Map(),
              params.enabledCategories,
              params.stapleNames,
              params.keptUtilityNames,
              nameToCard,
            );

        const selectionInput: KarstenSelectionInput = {
          candidates,
          colorIdentity,
          totalLandSlots: params.targetCount,
          stapleNames: params.stapleNames,
          keptUtilityNames: params.keptUtilityNames,
          minBasicsPerColor: params.minBasicsPerColor,
          categoryConstraints: params.categoryConstraints ?? new Map(),
          spells,
          commanders,
          mode: params.mode,
          lockedLands,
        };

        // Run selection synchronously (instant — Karsten-based, no worker)
        const selectionResult = selectLandsKarsten(selectionInput);

        const karstenTargets = selectionResult.targets;

        // Build Karsten source rows (land-only)
        const karstenRows: KarstenSourceRow[] = buildKarstenSourceRows(
          colorIdentity,
          selectionResult.landSources,
          karstenTargets,
        );

        // Build mana overview rows (land-only)
        const totalLandCount = selectionResult.selectedNames.length;
        const manaOverviewRows: ManaOverviewRow[] = buildManaOverviewRows(
          colorIdentity,
          pipCounts,
          selectionResult.landSources,
          karstenTargets,
          totalLandCount,
        );

        // Compute land diff
        const afterMultiset: LandMultiset = new Map();
        for (const name of selectionResult.selectedNames) {
          afterMultiset.set(name, (afterMultiset.get(name) ?? 0) + 1);
        }
        const landDiff = computeLandDiff(beforeSnapshot, afterMultiset);

        // Store panel data for the modal effect to pick up
        setPanelData({
          karstenRows,
          manaOverviewRows,
          landDiff,
          beforeSnapshot,
        });

        // Build proposal and move to review
        const newProposal = buildProposalFromNames(
          selectionResult.selectedNames,
          existingDeckNames,
        );
        setProposal(newProposal);
        setStage('review');
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        setGenerationError(message);
      }
    },
    [
      entries,
      colorIdentity,
      hasTribalSupport,
      existingLandsByCategory,
    ],
  );

  // ── Review: remove proposed land ──
  const removeProposedLand = useCallback((name: string, basicColor?: Color) => {
    setProposal((prev) => {
      if (basicColor) {
        return prev.filter(
          (p) => !(p.category === 'basic' && p.basicColor === basicColor),
        );
      }
      return prev.filter((p) => p.name !== name);
    });
  }, []);

  // ── Review: adjust basic count ──
  const adjustBasicCount = useCallback((color: Color, delta: number) => {
    setProposal((prev) => {
      return prev
        .map((p) => {
          if (p.category === 'basic' && p.basicColor === color) {
            return { ...p, quantity: Math.max(0, p.quantity + delta) };
          }
          return p;
        })
        .filter((p) => !(p.category === 'basic' && p.quantity === 0));
    });
  }, []);

  // ── Basic count setter ──
  const setBasicCount = useCallback((n: number) => {
    setBasicCountLocal(Math.max(0, n));
  }, []);

  // ── F-028: Toggle MDFC ──
  const toggleMdfc = useCallback((name: string) => {
    setSelectedMdfcs((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  }, []);

  const basicSlots = countBasicSlots(proposal);
  const mdfcOverLimit = selectedMdfcs.size > basicSlots;

  // ── Accept ──
  const handleAccept = useCallback(
    (resolvedCards: Array<{ card: Card; quantity: number }>) => {
      if (mode === 'generate') {
        removeLandEntries();
      }
      bulkAddCards(resolvedCards);
    },
    [mode, removeLandEntries, bulkAddCards],
  );

  return {
    stage,
    goToConfigure,
    targetCount,
    setTargetCount,
    mode,
    setMode,
    allocations,
    toggleCategory,
    basicCount,
    setBasicCount,
    proposal,
    removeProposedLand,
    adjustBasicCount,
    startGeneration,
    generationError,
    hint,
    existingLandCount,
    existingLandsByCategory,
    existingBasicCount,
    existingUtilityCount,
    existingUtilityLandNames: existingUtilityData.names,
    handleAccept,
    hasTribalSupport,
    selectedMdfcs,
    toggleMdfc,
    mdfcOverLimit,
    panelData,
  };
}
